import React, { useEffect, useState } from 'react';
import { Card, Button, Space, Typography, Spin, Alert } from 'antd';
import { WechatOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, loading } = useAuth();
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 如果已登录，重定向到仪表板
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // 处理企微回调
  useEffect(() => {
    const code = searchParams.get('code');
    if (code && !user) {
      handleWeChatCallback(code);
    }
  }, [searchParams, user]);

  const handleWeChatCallback = async (code: string) => {
    try {
      setLoginLoading(true);
      setError(null);
      await login(code);
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      setError(error.response?.data?.detail || '登录失败，请重试');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleWeChatLogin = async () => {
    try {
      setLoginLoading(true);
      setError(null);
      
      // 构建回调URL
      const redirectUri = `${window.location.origin}/login`;
      
      // 获取企微OAuth URL
      const { oauth_url } = await authService.getWeChatOAuthUrl(redirectUri);
      
      // 跳转到企微授权页面
      window.location.href = oauth_url;
    } catch (error: any) {
      setError(error.response?.data?.detail || '获取授权链接失败');
      setLoginLoading(false);
    }
  };

  // 检查是否在企微环境中
  const isInWeChatBrowser = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('micromessenger');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#f0f2f5'
      }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{ 
          width: '100%', 
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ 
            fontSize: 48, 
            color: '#1890ff', 
            marginBottom: 16 
          }}>
            📋
          </div>
          <Title level={2} style={{ margin: 0, color: '#262626' }}>
            DMS企微审批
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            数据变更审批管理系统
          </Text>
        </div>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {!isInWeChatBrowser() && (
            <Alert
              message="建议在企业微信中打开此页面以获得最佳体验"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Button
            type="primary"
            size="large"
            icon={<WechatOutlined />}
            loading={loginLoading}
            onClick={handleWeChatLogin}
            style={{ 
              width: '100%',
              height: 48,
              fontSize: 16,
              background: '#07C160',
              borderColor: '#07C160'
            }}
          >
            企业微信登录
          </Button>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              使用企业微信账号登录，安全便捷
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Login;
